import { ReactElement, useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import {
	EuiFieldText,
	EuiFormRow,
	EuiLoadingSpinner,
	EuiPage,
	EuiPageBody,
	EuiPageHeader,
	EuiSpacer,
	EuiText,
} from '@equipmentshare/ds2'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Select from '@mui/material/Select'
import { heapEvent } from '@equipmentshare/heaper'
import {
	DimensionUom,
	ItemMasterVariantUom,
	WeightUnit,
	useUpdateItemMasterVariantMutation,
} from '../../../../graphql'
import activateItemMasterVariantMutation from '../../../../graphql/mutations/item-masters/activate-item-master-variant'
import useItemMasterDetailQuery from '../detail/hooks/useItemMasterQuery'
import FormFooter, { FORM_FOOTER_HEIGHT, ValidationSummary } from '../components/FormFooter'
import {
	isValidSiocQuantity,
	siocQuantityForPayload,
	siocToggleChanges,
	SIOC_QUANTITY_ERROR,
	isComplete,
	dimensionsForPayload,
	weightForPayload,
} from '../utils'

type VariantFields = {
	uom: ItemMasterVariantUom,
	conversionRate: string,
	dimensionLength: string,
	dimensionWidth: string,
	dimensionHeight: string,
	dimensionUnit: DimensionUom,
	weightAmount: string,
	weightUnit: WeightUnit,
	freightClass: string,
	nmfc: string,
	shippedInOriginalContainer: boolean,
	siocQuantity: string,
	isPolybagEligible: boolean,
}

const defaultVariantFields: VariantFields = {
	uom: ItemMasterVariantUom.Each,
	conversionRate: '',
	dimensionLength: '',
	dimensionWidth: '',
	dimensionHeight: '',
	dimensionUnit: DimensionUom.In,
	weightAmount: '',
	weightUnit: WeightUnit.Lb,
	freightClass: '',
	nmfc: '',
	shippedInOriginalContainer: false,
	siocQuantity: '',
	isPolybagEligible: false,
}

export default function ItemMasterEditVariant(): ReactElement {
	const { id = '', variantId = '' } = useParams<{ id: string, variantId: string }>()
	const navigate = useNavigate()
	const { itemMaster, loading, error } = useItemMasterDetailQuery( id )

	const [ fields, setFields ] = useState<VariantFields>( defaultVariantFields )

	const variant = itemMaster?.variants.find( ( v ) => v.id === variantId )

	useEffect( () => {
		if ( variant ) {
			setFields( {
				uom: variant.uom,
				conversionRate: variant.conversionRate != null ? String( variant.conversionRate ) : '',
				dimensionLength: variant.dimensions?.length != null ? String( variant.dimensions.length ) : '',
				dimensionWidth: variant.dimensions?.width != null ? String( variant.dimensions.width ) : '',
				dimensionHeight: variant.dimensions?.height != null ? String( variant.dimensions.height ) : '',
				dimensionUnit: variant.dimensions?.unit ?? DimensionUom.In,
				weightAmount: variant.weight?.amount != null ? String( variant.weight.amount ) : '',
				weightUnit: variant.weight?.unit ?? WeightUnit.Lb,
				freightClass: variant.freightClass ?? '',
				nmfc: variant.nmfc ?? '',
				shippedInOriginalContainer: variant.shippedInOriginalContainer,
				siocQuantity: variant.siocQuantity != null ? String( variant.siocQuantity ) : '',
				isPolybagEligible: variant.isPolybagEligible ?? false,
			} )
		}
	}, [ variant ] )

	const [ updateVariant, { loading: saving } ] = useUpdateItemMasterVariantMutation()
	// Raw useMutation (not a generated hook) because the ActivateItemMasterVariant operation
	// lands with inventory-api Phase 1; swap to useActivateItemMasterVariantMutation after
	// `yarn codegen` runs against staging.
	const [ activateVariant ] = useMutation( activateItemMasterVariantMutation )

	const handleFieldChange = ( updated: Partial<VariantFields> ) => {
		setFields( ( prev ) => ( { ...prev, ...updated } ) )
	}

	const isValidSioc = isValidSiocQuantity( fields )
	const [ submitted, setSubmitted ] = useState( false )

	const recordComplete = isComplete( fields )
	const submitLabel = recordComplete ? 'Save Changes' : 'Save Draft'

	const summary: ValidationSummary | null = ( submitted && !isValidSioc )
		? { title: 'Fix the following to save:', errors: [ { field: 'siocQuantity', message: SIOC_QUANTITY_ERROR } ] }
		: null

	const handleSubmit = async() => {
		setSubmitted( true )
		if ( !isValidSioc ) {
			return
		}

		try {
			const result = await updateVariant( {
				variables: {
					input: {
						itemMasterVariantId: variantId,
						uom: fields.uom,
						conversionRate: ( !variant?.isBase && fields.conversionRate )
							? Number( fields.conversionRate )
							: undefined,
						// Omitted when blank so a draft variant stays a draft instead of being sent as 0.
						dimensions: dimensionsForPayload( fields ),
						weight: weightForPayload( fields ),
						freightClass: fields.freightClass || undefined,
						nmfc: fields.nmfc || undefined,
						shippedInOriginalContainer: fields.shippedInOriginalContainer,
						siocQuantity: siocQuantityForPayload( fields ),
						isPolybagEligible: fields.isPolybagEligible,
					},
				},
			} )

			if ( result.data?.updateItemMasterVariant?.success ) {
				// The update fills weight/dimensions but the backend leaves the variant DRAFT;
				// activate it once complete so "add the missing measurements" reads as
				// "completed". Idempotent server-side, so it is a no-op if already ACTIVE.
				if ( recordComplete ) {
					await activateVariant( { variables: { input: { itemMasterVariantId: variantId } } } )
				}
				heapEvent( 'Inventory - Item Master - Edit Variant - Submit - Success', { item_master_id: id } )
				navigate( `/manage/item-masters/${id}` )
			} else {
				heapEvent( 'Inventory - Item Master - Edit Variant - Submit - Error', {} )
			}
		} catch {
			heapEvent( 'Inventory - Item Master - Edit Variant - Submit - Error', {} )
		}
	}

	if ( loading ) {
		return (
			<EuiPage paddingSize="l">
				<EuiPageBody>
					<EuiLoadingSpinner size="xl" />
				</EuiPageBody>
			</EuiPage>
		)
	}

	if ( error || !itemMaster || !variant ) {
		return (
			<EuiPage paddingSize="l">
				<EuiPageBody>
					<EuiText><p>{error ? error.message : 'Variant not found.'}</p></EuiText>
				</EuiPageBody>
			</EuiPage>
		)
	}

	const productName = variant.product?.name ?? `Variant #${variantId}`

	return (
		<EuiPage paddingSize="l">
			<EuiPageBody>
				<EuiPageHeader
					pageTitle={`Edit Variant: ${productName}`}
					paddingSize="none"
				/>
				{variant.isBase && (
					<Box mt={1} mb={2}>
						<Typography variant="body2" color="text.secondary">
							This is the base variant. Conversion rate cannot be changed.
						</Typography>
					</Box>
				)}
				<EuiSpacer />

				<Box maxWidth={560} sx={{ pb: `${FORM_FOOTER_HEIGHT}px` }}>
					{variant.isBase && (
						<Typography variant="body2" color="text.secondary" mb={2}>
							Weight and dimensions can be left blank to keep this a draft. Fill them in to complete the item master and enable adding it to a distribution center.
						</Typography>
					)}
					<Stack spacing={2}>
						<Box>
							<Typography variant="caption" display="block" color="text.secondary" mb={0.5}>Unit of Measure</Typography>
							<Select
								value={fields.uom}
								onChange={( e ) => handleFieldChange( { uom: e.target.value as ItemMasterVariantUom } )}
								size="small"
								fullWidth
							>
								{Object.values( ItemMasterVariantUom ).map( ( u ) => (
									<MenuItem key={u} value={u}>{u}</MenuItem>
								) )}
							</Select>
						</Box>

						{!variant.isBase && (
							<EuiFormRow label="Conversion Rate" helpText="Number of base units per this variant (minimum 2)">
								<EuiFieldText
									value={fields.conversionRate}
									onChange={( e ) => handleFieldChange( { conversionRate: e.target.value } )}
									type="number"
								/>
							</EuiFormRow>
						)}

						<Typography variant="subtitle2">Dimensions</Typography>
						<Stack direction="row" spacing={2}>
							<EuiFormRow label="Length">
								<EuiFieldText
									value={fields.dimensionLength}
									onChange={( e ) => handleFieldChange( { dimensionLength: e.target.value } )}
									type="number"
								/>
							</EuiFormRow>
							<EuiFormRow label="Width">
								<EuiFieldText
									value={fields.dimensionWidth}
									onChange={( e ) => handleFieldChange( { dimensionWidth: e.target.value } )}
									type="number"
								/>
							</EuiFormRow>
							<EuiFormRow label="Height">
								<EuiFieldText
									value={fields.dimensionHeight}
									onChange={( e ) => handleFieldChange( { dimensionHeight: e.target.value } )}
									type="number"
								/>
							</EuiFormRow>
							<Box>
								<Typography variant="caption" display="block" color="text.secondary" mb={0.5}>Unit</Typography>
								<Select
									value={fields.dimensionUnit}
									onChange={( e ) => handleFieldChange( { dimensionUnit: e.target.value as DimensionUom } )}
									size="small"
								>
									{Object.values( DimensionUom ).map( ( u ) => (
										<MenuItem key={u} value={u}>{u}</MenuItem>
									) )}
								</Select>
							</Box>
						</Stack>

						<Typography variant="subtitle2">Weight</Typography>
						<Stack direction="row" spacing={2} alignItems="flex-start">
							<EuiFormRow label="Amount">
								<EuiFieldText
									value={fields.weightAmount}
									onChange={( e ) => handleFieldChange( { weightAmount: e.target.value } )}
									type="number"
								/>
							</EuiFormRow>
							<Box>
								<Typography variant="caption" display="block" color="text.secondary" mb={0.5}>Unit</Typography>
								<Select
									value={fields.weightUnit}
									onChange={( e ) => handleFieldChange( { weightUnit: e.target.value as WeightUnit } )}
									size="small"
								>
									{Object.values( WeightUnit ).map( ( u ) => (
										<MenuItem key={u} value={u}>{u}</MenuItem>
									) )}
								</Select>
							</Box>
						</Stack>

						<EuiFormRow label="Freight Class">
							<EuiFieldText
								value={fields.freightClass}
								onChange={( e ) => handleFieldChange( { freightClass: e.target.value } )}
							/>
						</EuiFormRow>

						<EuiFormRow label="NMFC">
							<EuiFieldText
								value={fields.nmfc}
								onChange={( e ) => handleFieldChange( { nmfc: e.target.value } )}
							/>
						</EuiFormRow>

						<FormControlLabel
							control={
								<Checkbox
									checked={fields.shippedInOriginalContainer}
									onChange={( e ) => handleFieldChange( siocToggleChanges( e.target.checked ) )}
								/>
							}
							label="Shipped in Original Container"
						/>

						<EuiFormRow
							label="SIOC Quantity"
							isInvalid={!isValidSioc}
							error={!isValidSioc ? SIOC_QUANTITY_ERROR : undefined}
						>
							<EuiFieldText
								value={fields.shippedInOriginalContainer ? fields.siocQuantity : ''}
								onChange={( e ) => handleFieldChange( { siocQuantity: e.target.value } )}
								type="number"
								min={1}
								disabled={!fields.shippedInOriginalContainer}
								isInvalid={!isValidSioc}
							/>
						</EuiFormRow>

						<FormControlLabel
							control={
								<Checkbox
									checked={fields.isPolybagEligible}
									onChange={( e ) => handleFieldChange( { isPolybagEligible: e.target.checked } )}
									disabled={fields.shippedInOriginalContainer}
								/>
							}
							label="Polybag Eligible"
						/>

					</Stack>
				</Box>

				<FormFooter summary={summary}>
					<Button
						variant="outlined"
						onClick={() => navigate( `/manage/item-masters/${id}` )}
						disabled={saving}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={saving || !isValidSioc}
					>
						{saving ? 'Saving...' : submitLabel}
					</Button>
				</FormFooter>
			</EuiPageBody>
		</EuiPage>
	)
}
